import { useEffect, useState } from 'react'
import { onValue, ref, update } from 'firebase/database'
import { db, tref } from '../../lib/firebase.js'
import CLUB from '../../config/club.js'
import { GGC_ONLY } from '../../config/siteMode.js'

// Access-code gate for the isolated G-New Features area. Mirrors PinGate's
// prompt/compare/subscribe mechanics but with its OWN code, held in the DB at
// config/ggAccess/code (shareable across devices). Unlock is per browser
// session (sessionStorage) so it re-prompts each session. The ONLY GG write is
// setting this code — a config node, never tournament/scoring/photo data.
const UNLOCK_KEY = 'ggUnlocked'
const DEFAULT_GG_CODE = 'golf' // change immediately via "Access code" below

const readUnlocked = () => {
  try { return sessionStorage.getItem(UNLOCK_KEY) === 'true' } catch { return false }
}

export default function GGPinGate({ children }) {
  // FULL ACCESS: the Pro Shop Suite is open — no access-code barrier. Every
  // module is reachable immediately. (The suite only exists on the full build,
  // so this never affects the 4-Ball / club sites.) The "Access code" footer
  // is still available if a holder later wants to lock it. Start unlocked and
  // pre-seed the code so there's no loading flash / prompt.
  const [code, setCode] = useState(DEFAULT_GG_CODE)
  const [unlocked, setUnlocked] = useState(true)
  const [entry, setEntry] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    const off = onValue(
      tref('config/ggAccess/code'),
      (snap) => { const v = snap.val(); setCode(typeof v === 'string' && v.length ? v : DEFAULT_GG_CODE) },
      () => setCode(DEFAULT_GG_CODE),
    )
    return () => off()
  }, [])

  if (code === undefined) return null

  if (unlocked) {
    return (
      <>
        {children}
        {/* The access-code footer (⚠ default-code warning + 🔑 Access code) is a
            staff control — hidden on the standalone members' GGC. */}
        {!GGC_ONLY && <CodeFooter current={code} isDefault={code === DEFAULT_GG_CODE} />}
      </>
    )
  }

  const submit = (e) => {
    e.preventDefault()
    if (entry.trim() && entry.trim() === code) {
      try { sessionStorage.setItem(UNLOCK_KEY, 'true') } catch {}
      setUnlocked(true); setEntry(''); setError('')
    } else {
      setError('Incorrect code.')
    }
  }

  return (
    <div className="card max-w-sm mx-auto text-center" style={{ backgroundColor: CLUB.primaryColor, color: CLUB.creamColor }}>
      <div className="text-3xl mb-2">🔒</div>
      <h3 className="font-bold text-lg" style={{ color: CLUB.accentColor }}>G-New Features</h3>
      <p className="text-sm mb-4" style={{ color: `${CLUB.creamColor}bb` }}>
        In-progress staging area. Enter the access code to continue.
      </p>
      <form onSubmit={submit} className="flex flex-col gap-2">
        <input
          autoFocus
          type="password"
          value={entry}
          onChange={(e) => { setEntry(e.target.value); setError('') }}
          placeholder="Access code"
          className="rounded-lg px-3 py-2 text-center text-black"
        />
        {error ? <p className="text-sm" style={{ color: '#F87171' }}>{error}</p> : null}
        <button type="submit" className="rounded-lg px-4 py-2 font-bold" style={{ backgroundColor: CLUB.accentColor, color: CLUB.primaryColor }}>
          Unlock
        </button>
      </form>
    </div>
  )
}

// Holder-only control to set/change the shared access code (config write).
function CodeFooter({ current, isDefault }) {
  const [open, setOpen] = useState(false)
  const [val, setVal] = useState(current)
  const [saved, setSaved] = useState(false)
  const save = async (e) => {
    e.preventDefault()
    const clean = val.trim()
    if (!clean) return
    await update(tref('config/ggAccess'), { code: clean })
    setSaved(true)
    setTimeout(() => setSaved(false), 1500)
  }
  return (
    <div className="mt-4 text-xs text-gray-500">
      {isDefault ? (
        <p className="mb-1" style={{ color: '#B45309' }}>⚠ Still the default code — set your own so only people you share it with can get in.</p>
      ) : null}
      <button className="underline" onClick={() => setOpen((v) => !v)}>🔑 Access code</button>
      {open ? (
        <form onSubmit={save} className="mt-2 flex items-center gap-2">
          <input value={val} onChange={(e) => setVal(e.target.value)} className="rounded border px-2 py-1 text-sm" placeholder="New access code" />
          <button type="submit" className="btn-ghost text-xs">Save</button>
          {saved ? <span className="text-green-deep">Saved ✓</span> : null}
        </form>
      ) : null}
    </div>
  )
}
